(() => {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  let leaving = false;

  const initVelaeCarousels = () => {
    document.querySelectorAll('[data-velae-carousel]').forEach((rail) => {
      if (rail.dataset.velaeCarouselReady === 'true') return;

      const section = rail.closest('.velae-occasion-edit, .related-products, .velae-product-edit, .velae-social-edit, .velae-product-testimonials');
      const previousButton = section?.querySelector('[data-velae-carousel-prev]');
      const nextButton = section?.querySelector('[data-velae-carousel-next]');
      const itemSelector = rail.dataset.velaeCarouselItemSelector || '.velae-occasion-edit__tile';

      if (!section) return;

      rail.dataset.velaeCarouselReady = 'true';

      let loopWidth = 0;
      const loopEnabled = rail.dataset.velaeCarouselAutoplay === 'true' && !reducedMotion.matches && !window.Shopify?.designMode;

      const removeDuplicateAttributes = (node) => {
        if (node.nodeType !== Node.ELEMENT_NODE) return;

        node.removeAttribute('id');
        node.querySelectorAll('[id]').forEach((element) => element.removeAttribute('id'));
      };

      const silenceCloneFocus = (node) => {
        node.setAttribute('aria-hidden', 'true');
        node.setAttribute('inert', '');
        node.dataset.velaeCarouselClone = 'true';
        node.querySelectorAll('a, button, input, select, textarea, [tabindex]').forEach((element) => {
          element.setAttribute('tabindex', '-1');
        });
        node.querySelectorAll('button, input, select, textarea').forEach((element) => {
          element.disabled = true;
        });
      };

      const refreshLoopWidth = () => {
        if (!loopEnabled) return;

        const originalCount = parseInt(rail.dataset.velaeCarouselOriginalCount, 10);
        const firstItem = rail.children[0];
        const firstClone = rail.children[originalCount];

        loopWidth = firstItem && firstClone ? firstClone.offsetLeft - firstItem.offsetLeft : 0;
      };

      const prepareInfiniteLoop = () => {
        if (!loopEnabled || rail.dataset.velaeCarouselLooped === 'true') return;

        const originalItems = Array.from(rail.children);
        if (originalItems.length < 2) return;

        rail.dataset.velaeCarouselLooped = 'true';
        rail.dataset.velaeCarouselOriginalCount = String(originalItems.length);
        rail.classList.add('velae-carousel--looping');
        rail.style.scrollBehavior = 'auto';
        rail.style.scrollSnapType = 'none';

        originalItems.forEach((item) => {
          const clone = item.cloneNode(true);
          removeDuplicateAttributes(clone);
          silenceCloneFocus(clone);
          rail.appendChild(clone);
        });

        refreshLoopWidth();
      };

      const getStep = () => {
        const firstTile = rail.querySelector(itemSelector);
        const gap = parseFloat(window.getComputedStyle(rail).columnGap || '0');
        return firstTile ? firstTile.getBoundingClientRect().width + gap : rail.clientWidth * 0.85;
      };

      const getMaxScroll = () => rail.scrollWidth - rail.clientWidth - 2;

      const getAutoplaySpeed = () => {
        const configuredSpeed = parseFloat(rail.dataset.velaeCarouselSpeed);

        if (configuredSpeed > 0) return configuredSpeed;

        const intervalSeconds = Math.max((parseInt(rail.dataset.velaeCarouselInterval, 10) || 4000) / 1000, 3);
        const naturalSpeed = getStep() / intervalSeconds;

        if (window.matchMedia('(max-width: 749px)').matches) {
          return Math.min(Math.max(naturalSpeed * 1.55, 96), 180);
        }

        if (window.matchMedia('(max-width: 989px)').matches) {
          return Math.min(Math.max(naturalSpeed * 1.25, 72), 150);
        }

        return Math.min(Math.max(naturalSpeed, 34), 96);
      };

      const updateControls = () => {
        if (!previousButton || !nextButton) return;

        if (loopWidth) {
          const disabled = loopWidth <= rail.clientWidth + 2;
          previousButton.disabled = disabled;
          nextButton.disabled = disabled;
          return;
        }

        const maxScroll = getMaxScroll();
        previousButton.disabled = rail.scrollLeft <= 2;
        nextButton.disabled = rail.scrollLeft >= maxScroll;
      };

      const scrollNext = () => {
        refreshLoopWidth();

        const maxScroll = loopWidth || getMaxScroll();
        const behavior = reducedMotion.matches ? 'auto' : 'smooth';

        if (maxScroll <= 0) return;

        if (loopWidth && rail.scrollLeft >= loopWidth - getStep()) {
          rail.scrollTo({ left: Math.max(rail.scrollLeft - loopWidth, 0), behavior: 'auto' });
        } else if (!loopWidth && rail.scrollLeft >= maxScroll) {
          rail.scrollTo({ left: 0, behavior });
          return;
        }

        rail.scrollBy({ left: getStep(), behavior });
      };

      let autoplayFrame;
      let lastAutoplayTime;
      let resumeAutoplayTimer;

      const autoplayStep = (timestamp) => {
        if (!document.documentElement.contains(rail)) {
          stopAutoplay();
          return;
        }

        const maxScroll = getMaxScroll();
        refreshLoopWidth();
        const loopEnd = loopWidth || maxScroll;

        if (loopEnd <= 0 || document.hidden) {
          lastAutoplayTime = timestamp;
          autoplayFrame = window.requestAnimationFrame(autoplayStep);
          return;
        }

        if (!lastAutoplayTime) lastAutoplayTime = timestamp;

        const elapsed = Math.min(timestamp - lastAutoplayTime, 64);
        const speed = getAutoplaySpeed();
        const nextScroll = rail.scrollLeft + (elapsed * speed) / 1000;

        if (loopWidth && nextScroll >= loopWidth) {
          rail.scrollTo({ left: nextScroll - loopWidth, behavior: 'auto' });
        } else if (!loopWidth && nextScroll >= maxScroll) {
          rail.scrollTo({ left: 0, behavior: 'auto' });
        } else {
          rail.scrollLeft = nextScroll;
        }

        lastAutoplayTime = timestamp;
        autoplayFrame = window.requestAnimationFrame(autoplayStep);
      };

      const stopAutoplay = () => {
        window.clearTimeout(resumeAutoplayTimer);
        if (!autoplayFrame) return;
        window.cancelAnimationFrame(autoplayFrame);
        autoplayFrame = undefined;
        lastAutoplayTime = undefined;
      };

      const startAutoplay = () => {
        const autoplayEnabled = rail.dataset.velaeCarouselAutoplay === 'true';

        if (!autoplayEnabled || reducedMotion.matches || window.Shopify?.designMode || autoplayFrame) return;

        autoplayFrame = window.requestAnimationFrame(autoplayStep);
      };

      const resumeAutoplay = () => {
        window.clearTimeout(resumeAutoplayTimer);
        startAutoplay();
      };

      if (previousButton) {
        previousButton.addEventListener('click', () => {
          refreshLoopWidth();

          if (loopWidth && rail.scrollLeft <= 2) {
            rail.scrollTo({ left: loopWidth, behavior: 'auto' });
          }

          rail.scrollBy({ left: -getStep(), behavior: reducedMotion.matches ? 'auto' : 'smooth' });
        });
      }

      if (nextButton) {
        nextButton.addEventListener('click', () => {
          scrollNext();
        });
      }

      section.addEventListener('pointerenter', stopAutoplay);
      section.addEventListener('pointerleave', resumeAutoplay);
      rail.addEventListener('focusin', stopAutoplay);
      rail.addEventListener('focusout', resumeAutoplay);
      rail.addEventListener('touchstart', stopAutoplay, { passive: true });
      rail.addEventListener('touchend', resumeAutoplay, { passive: true });
      rail.addEventListener('scroll', updateControls, { passive: true });
      window.addEventListener('resize', () => {
        refreshLoopWidth();
        updateControls();
      });
      prepareInfiniteLoop();
      updateControls();
      startAutoplay();
    });
  };

  const finishEntry = () => {
    document.body?.classList.remove('velae-entry-pending');
    document.body?.classList.add('velae-entry-ready');
  };

  const openSizeGuideLightbox = (imageUrl, altText) => {
    if (!imageUrl) return;

    document.querySelector('[data-velae-size-guide-lightbox]')?.remove();

    const lightbox = document.createElement('div');
    lightbox.className = 'velae-size-guide-lightbox';
    lightbox.dataset.velaeSizeGuideLightbox = 'true';
    lightbox.setAttribute('role', 'dialog');
    lightbox.setAttribute('aria-modal', 'true');
    lightbox.setAttribute('aria-label', 'Guia de tallas ampliada');

    const content = document.createElement('div');
    content.className = 'velae-size-guide-lightbox__content';

    const closeButton = document.createElement('button');
    closeButton.type = 'button';
    closeButton.className = 'velae-size-guide-lightbox__close';
    closeButton.setAttribute('aria-label', 'Cerrar guia de tallas');
    closeButton.textContent = '×';

    const image = document.createElement('img');
    image.className = 'velae-size-guide-lightbox__image';
    image.src = imageUrl;
    image.alt = altText || 'Guia de tallas VELAE';
    image.width = 1279;
    image.height = 1600;

    const caption = document.createElement('p');
    caption.className = 'velae-size-guide-lightbox__caption';
    caption.textContent = 'Toca fuera de la imagen o presiona Escape para cerrar.';

    const closeLightbox = () => {
      document.removeEventListener('keydown', handleKeydown);
      lightbox.remove();
    };

    const handleKeydown = (event) => {
      if (event.key === 'Escape') closeLightbox();
    };

    lightbox.addEventListener('click', (event) => {
      if (event.target === lightbox) closeLightbox();
    });
    closeButton.addEventListener('click', closeLightbox);
    document.addEventListener('keydown', handleKeydown);

    content.append(closeButton, image, caption);
    lightbox.append(content);
    document.body.append(lightbox);
    closeButton.focus();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initVelaeCarousels, { once: true });
  } else {
    initVelaeCarousels();
  }

  document.addEventListener('shopify:section:load', initVelaeCarousels);

  const carouselObserver = new MutationObserver(initVelaeCarousels);
  carouselObserver.observe(document.body, { childList: true, subtree: true });

  if (reducedMotion.matches || window.Shopify?.designMode) {
    finishEntry();
    return;
  }

  requestAnimationFrame(() => {
    requestAnimationFrame(finishEntry);
  });

  const isSafeInternalNavigation = (link, event) => {
    if (!link || leaving) return false;
    if (event.defaultPrevented || event.button !== 0) return false;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return false;
    if (link.target && link.target !== '_self') return false;
    if (link.hasAttribute('download')) return false;
    if (link.closest('form, product-form, cart-drawer, .cart-drawer, .shopify-payment-button, .mobile-facets, .facets')) return false;

    const url = new URL(link.href, window.location.href);
    if (url.origin !== window.location.origin) return false;
    if (url.hash && url.pathname === window.location.pathname) return false;
    if (url.pathname.includes('/cart') || url.pathname.includes('/checkout')) return false;

    return url.pathname === '/' || url.pathname.startsWith('/collections') || url.pathname.startsWith('/products') || url.pathname.startsWith('/pages');
  };

  document.addEventListener(
    'click',
    (event) => {
      const sizeGuideButton = event.target.closest('[data-velae-size-guide-zoom]');
      if (sizeGuideButton) {
        event.preventDefault();
        openSizeGuideLightbox(sizeGuideButton.dataset.imageSrc, sizeGuideButton.dataset.imageAlt);
        return;
      }

      const link = event.target.closest('a[href]');
      if (!isSafeInternalNavigation(link, event)) return;

      event.preventDefault();
      leaving = true;
      document.body.classList.add('velae-page-exiting');

      window.setTimeout(() => {
        window.location.href = link.href;
      }, 320);
    },
    { capture: true }
  );
})();
